import { css, html, TemplateResult } from 'lit'
import { property } from 'lit/decorators/property.js'
import '@pushengineering/push-web-components/text'
import '@pushengineering/push-web-components/button'
import '@pushengineering/push-web-components/vertical-stack'
import '@pushengineering/push-web-components/header-drawer'
import '@pushengineering/push-web-components/link'
import '@pushengineering/push-web-components/logo'

import CirkayElement from '../../internal/CirkayElement'
import { FpSession, getLatestSession } from '../../utils/helpers'
import { LoginController } from '../../controllers/LoginController'
import { MagicOptions } from '../../base/magic/MagicAPI'
import { Environment } from '../../base/n3xus/types'
import { state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { LoginEvents } from '../../events/login'

interface FormChangeDetail {
    formData: Record<string, any>
    changedField: {
        name: string
        value: string | boolean
    }
    userEmail?: string | null | undefined
}

export class Connected extends CirkayElement {
    private loginController: LoginController | null = null

    @property({ type: String }) environment: Environment = Environment.PRODUCTION
    @property({ type: String }) apiKey = ''
    @property({ type: String }) fanPassTitle = 'test'
    @property() displayText: String | null = null
    @property({ type: Boolean }) debugLogging = false
    @property({ type: Boolean }) showMobilePassButtons = false

    @state()
    _popoverOpen = false

    @state()
    _fpSession: FpSession | null = null

    constructor() {
        super()
        this.addEventListener('focusout', this._popoverFocusOut)
    }

    connectedCallback() {
        super.connectedCallback()

        const magicApiOptions: MagicOptions = {
            environment: this.environment,
        }

        const session = getLatestSession(this.apiKey)
        const minutesAgo = Date.now() - 3.5 * 60 * 1000

        if (session && !(session.issueTS < minutesAgo)) {
            this._fpSession = session
        }

        this.loginController = LoginController.getInstance(
            magicApiOptions,
            this.debugLogging
        )

        this.loginController.addHost(this)
        this._setupFormListener()
    }

    disconnectedCallback() {
        super.disconnectedCallback?.()
        this._removeFormListener()
    }

    private _removeFormListener(): void {
        const formSlot = this.querySelector('[slot="form-content"]')
        if (formSlot) {
            const form = formSlot.querySelector('form')
            if (form) {
                const formElements = form.querySelectorAll<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >('input, select, textarea')
                formElements.forEach((element) => {
                    element.removeEventListener(
                        'change',
                        this._handleFormChange
                    )
                })
            }
        }
    }

    private async handleLogout() {
        this.emit(LoginEvents.Logout, {
            bubbles: true,
            composed: true,
            detail: {
                LoggedIn: false,
            },
        })
        const actionMenu = this.shadowRoot?.querySelector(
            '[data-fp-action-menu]'
        )
        if (actionMenu) {
            actionMenu.setAttribute('disabled', 'disabled')
        }
        await this.loginController?.logout()
    }

    private _popoverFocusOut(e: FocusEvent) {
        if (!e.relatedTarget || !this.contains(e.relatedTarget as Node)) {
            this._popoverOpen = false
        }
    }

    private _toggleSuccessPopover() {
        this._popoverOpen = !this._popoverOpen ? true : false
    }

    private _setupFormListener() {
        // Look for a form in the form slot
        const formSlot = this.querySelector('[slot="form-content"]')
        if (formSlot) {
            const form = formSlot.querySelector('form')
            if (form) {
                // Listen for changes on all form elements
                form.querySelectorAll('input, select, textarea').forEach(
                    (element) => {
                        element.addEventListener(
                            'change',
                            this._handleFormChange
                        )
                    }
                )
            }
        }
    }

    private _handleFormChange = (event: Event): void => {
        const target = event.target as
            | HTMLInputElement
            | HTMLSelectElement
            | HTMLTextAreaElement
        const form = target.closest('form')

        if (!form) return

        const formData = new FormData(form)
        const formObject: Record<string, any> = {}

        formData.forEach((value, key) => {
            formObject[key] = value
        })

        const changedField = {
            name: target.name,
            value:
                target instanceof HTMLInputElement && target.type === 'checkbox'
                    ? target.checked
                    : target.value,
        }

        const detail: FormChangeDetail = {
            formData: formObject,
            changedField,
            userEmail: this.loginController?.user?.email,
        }

        this.dispatchEvent(
            new CustomEvent<FormChangeDetail>(LoginEvents.Submit, {
                detail,
                bubbles: true,
                composed: true,
            })
        )
    }

    private renderPopoverMenu(): TemplateResult {
        if (!this._popoverOpen) {
            return html``
        }
        return html`
            <div
                part="popover-container"
                id="popover-content"
                class="popover__content popover-open-direction--down"
            >
                <button
                    part="button-icon"
                    class="popover__close-button"
                    type="button"
                    aria-label="Close popover"
                    aria-controls="popover-content"
                    @click=${this._toggleSuccessPopover}
                >
                    <svg
                        part="ellipsis-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        id="close"
                        class="icon"
                        width="1em"
                        height="1em"
                        viewBox="0 0 384 512"
                        role="presentation"
                        aria-hidden="true"
                        focusable="false"
                        fill="currentColor"
                    >
                        <path
                            d="M192 233.4L59.5 100.9 36.9 123.5 169.4 256 36.9 388.5l22.6 22.6L192 278.6 324.5 411.1l22.6-22.6L214.6 256 347.1 123.5l-22.6-22.6L192 233.4z"
                        />
                    </svg>
                </button>
                <div
                    part="popover-content"
                    class="popover__content-inner"
                >
                    <pu-button
                        exportparts="button:button-link,button:disconnect-link"
                        part="base-button-link base-disconnect-link"
                        class="disconnect-button"
                        @click=${this.handleLogout}
                        type="button"
                        name="disconnect"
                        size="small"
                        variant="plain"
                        fullWidth
                    >
                        <pu-text
                            exportparts="base:button-link-label,base:disconnect-link-label"
                            part="base-button-link-label base-disconnect-link-label"
                            as="span"
                            textAlign="center"
                            variant="bodyMd"
                            fontWeight="regular"
                            withoutMargin
                            spacing="loose"
                        >
                            Disconnect
                        </pu-text>
                    </pu-button>
                </div>
            </div>
        `
    }

    public render(): TemplateResult {
        if (
            this.loginController?.loginInit &&
            !this.loginController?.loggedIn
        ) {
            return html``
        }
        if (!this._fpSession && !this.loginController?.loginInit) {
            return html``
        }

        const displayText = this.displayText
            ? this.displayText
            : 'Invalid connection'

        return html`
            <pu-box
                exportparts="base:account-connected,content:account-connected-content"
                part="base-account-connected"
                class="success-container"
                padding="16, 24"
                paddingNarrow=${16}
                border
            >
                <pu-vertical-stack
                    exportparts="base:account-connected-layout"
                    part="base-account-connected-layout"
                    align="center"
                    spacing="8"
                    spacingNarrow="8"
                    >
                    <pu-text
                        exportparts="base:base-body,base:account-connected-text"
                        part="base-account-connected-text base-text base-body"
                        as="p"
                        variant="headingLg"
                        fontWeight="bold"
                        withoutMargin
                    >
                        ${this.loginController?.user?.email
                            ? displayText
                            : html`<svg
                                  part="connecting-spinner"
                                  class="loading__spinner"
                                  width="16"
                                  height="16"
                                  xmlns="http://www.w3.org/2000/svg"
                                  focusable="false"
                                  role="presentation"
                                  aria-hidden="true"
                                  viewBox="0 0 512 512"
                              >
                                  <path
                                      d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                                  />
                              </svg>`}
                    </pu-text>

                    <slot name="inner-content"></slot>

                    ${this.loginController?.user?.email
                        ? html`
                              <pu-text
                                  exportparts="base:account-user-email"
                                  part="base-account-user-email base-text base-body"
                                  as="p"
                                  variant="bodyXs"
                                  fontWeight="regular"
                                  withoutMargin
                              >
                                  connected as:
                                  ${this.loginController?.user?.email}
                              </pu-text>
                          `
                        : null}
                    ${this.loginController?.user?.email &&
                    this._fpSession?.mobilePass &&
                    this.showMobilePassButtons
                        ? html`
                              <fp-mobile-pass
                                exportparts="
                                    base-text,
                                    base-title,
                                    base-body,
                                    mobile-pass,
                                    mobile-pass-title
                                    mobile-pass-action-text
                                    mobile-pass-link,
                                    apple-wallet-image,
                                    google-wallet-image,
                                    wallet-buttons"
                                    .fanPassTitle=${this.fanPassTitle}
                                    .androidLink=${Array.isArray(
                                        this._fpSession?.mobilePass
                                    ) && this._fpSession?.mobilePass.length
                                        ? this._fpSession.mobilePass[
                                                this._fpSession?.mobilePass
                                                    ?.length - 1
                                            ].androidWalletUri
                                        : ''}
                                    .appleLink=${Array.isArray(
                                        this._fpSession?.mobilePass
                                    ) && this._fpSession?.mobilePass.length
                                        ? this._fpSession.mobilePass[
                                                this._fpSession?.mobilePass
                                                    ?.length - 1
                                            ].iPhoneWalletUri
                                        : ''}
                                    .downloadLink=${Array.isArray(
                                        this._fpSession?.mobilePass
                                    ) && this._fpSession?.mobilePass.length
                                        ? this._fpSession.mobilePass[
                                                this._fpSession?.mobilePass
                                                    ?.length - 1
                                            ].downloadLink
                                        : ''}
                                    .displayIntro=${false}
                              ></fp-mobile-pass>
                          `
                        : null}

                    <pu-horizontal-stack
                        part="base-powered-by-layout"
                        exportparts="base:powered-by-layout"
                        spacing="4"
                        align="center"
                        alignNarrow="center"
                        justify="center"
                        justifyNarrow="center"
                    >
                        <pu-text
                            exportparts="base:powered-by-text"
                            part="base-powered-by-text base-text base-body"
                            class="powered-by-text"
                            as="span"
                            variant="bodyXs"
                            withoutMargin
                            >
                            Powered by
                            <pu-link target="_blank" url="http://cirkay.com">Cirkay&reg;</pu-link>
                        </pu-text>
                        <pu-logo
                            class="cirkay-logo"
                            url="https://cirkay.com/"
                            target="_blank"
                            alt="Cirkay Logo"
                            height="12"
                            imgsrc="https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp"
                            imgsrcset="
                                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 4x,
                                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 3x,
                                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 2x,
                                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 1x
                                        "
                            imgsizes=""
                            imgwidth="12"
                            imgheight="12"
                        ></pu-logo>
                    </pu-horizontal-stack>
                    <div
                        part="popover-positioning-container"
                        class="popover-positioning-container"
                    >
                        <div
                            part="popover"
                            class="popover ${classMap({
                                'popover--open': this._popoverOpen,
                            })}"
                        >
                            <button
                                part="ellipsis-button"
                                class="ellipsis-button"
                                type="button"
                                data-fp-action-menu
                                ?disabled=${!this.loginController?.loggedIn ||
                                !this.loginController?.loginInit}
                                aria-label="Close popover"
                                aria-controls="popover-content"
                                @click=${this._toggleSuccessPopover}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    id="ellipsis-vertical"
                                    class="icon"
                                    width="0.75em"
                                    height="0.75em"
                                    viewBox="0 0 128 512"
                                    role="presentation"
                                    aria-hidden="true"
                                    focusable="false"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M64 384a32 32 0 1 0 0 64 32 32 0 1 0 0-64zm0-160a32 32 0 1 0 0 64 32 32 0 1 0 0-64zM96 96A32 32 0 1 0 32 96a32 32 0 1 0 64 0z"
                                    />
                                </svg>
                            </button>
                            ${this.renderPopoverMenu()}
                        </div>
                    </div>
                </pu-vertical-stack>
            </pu-box>
        `
    }

    static override styles = css`
        :host {
            font-size: var(--fp-font-size-base, 1em);
        }
        .cirkay-logo {
            width: 12px;
        }

        .powered-by-text {
            line-height: 1;
        }

        /* success box and popover */
        .success-container {
            --pu-border-base-corner-radius: var(--pu-border-radius-200, 8px);
            position: relative;
        }

        .popover-positioning-container {
            position: absolute;
            top: 0.25em;
            right: 0.25em;
        }

        .ellipsis-button {
            z-index: 499;
            cursor: pointer;
            border: none;
            border-radius: var(
                --pu-border-icon-corner-radius,
                var(--pu-border-base-corner-radius, 0)
            );
            font-size: var(--pu-font-size-icon, 1.5em);
            padding: 0;
            color: var(--pu-color-icon-text, inherit);
            background-color: var(--pu-color-icon-background, inherit);
        }

        .ellipsis-button:disabled {
            opacity: 0.2;
            pointer-events: none;
        }

        .popover {
            --pu-color-popover-text: rgb(0, 0, 0);
            position: relative;
        }

        .popover__content {
            width: max-content;

            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            z-index: 499; // under overlay z-index
            color: var(
                --pu-color-popover-text,
                var(--pu-color-base-text, rgb(255, 255, 255))
            );
            background-color: var(
                --pu-color-popover-background,
                var(--pu-color-base-background, rgb(255, 255, 255))
            );
            padding: 1.5em 0;
        }
        @supports (color: rgb(from white r g b)) {
            .popover__content {
                color: rgb(
                    from
                        var(
                            --pu-color-popover-text,
                            var(--pu-color-base-text, rgb(255, 255, 255))
                        )
                        r g b
                );
                background-color: rgb(
                    from
                        var(
                            --pu-color-popover-background,
                            var(--pu-color-base-background, rgb(255, 255, 255))
                        )
                        r g b / alpha
                );
            }
        }

        .popover__content-inner {
            display: flex;
            flex-direction: column;
            justify-content: start;
            align-items: center;
            text-align: center;
            gap: var(--pu-space-500, var(--pu-space-vertical-gap, 1.25em));
        }
        @media screen and (min-width: 48.06em) {
            .popover__content {
                position: absolute;
                left: initial;
                bottom: initial;
                top: 0.25em;
                right: 0.25em;
                border-radius: var(--pu-border-base-corner-radius, 0.5em);
                border: 1px solid var(--pu-color-base-border, rgb(0, 0, 0, 0.3));
                padding: 1em 1em 0.5em 1em;
                width: max-content;

                --pu-text-body-scale: 0.8;
            }

            .popover__content.popover-open-direction--down {
                bottom: initial;
                top: 0.25em;
            }

            .popover__content.popover-open-direction--up {
                bottom: 0;
                top: initial;
            }

            .popover__content-inner {
                gap: var(--pu-space-400, var(--pu-space-vertical-gap, 1em));
                width: fit-content;
            }
        }

        .popover__close-button {
            position: absolute;
            top: 0.5em;
            right: 0.5em;
            cursor: pointer;
            border: none;
            color: var(--pu-color-icon-text, inherit);
            background-color: var(--pu-color-icon-background, inherit);
            padding: 0;
        }
        @media screen and (min-width: 48.06em) {
            .popover__close-button {
                top: 0.5em;
                right: 0.5em;
                cursor: pointer;
                border: none;
            }
        }

        /* Mobile overlay */
        @media screen and (max-width: 48em) {
            .popover--open::after {
                content: '';
                position: fixed;
                inset: 0;
                background-color: rgb(0, 0, 0, 0.5);
                backdrop-filter: blur(2px);
                z-index: 498;
            }
        }

        /* buttons */
        .button::part(button) {
            --pu-border-button-corner-radius: var(--pu-border-radius-200, 8px);
        }

        .button-icon {
            position: absolute;
            top: 1em;
            right: 1em;
            z-index: 501;
            cursor: pointer;
            border: none;
            border-radius: var(
                --pu-border-icon-corner-radius,
                var(--pu-border-base-corner-radius, 0)
            );
            font-size: var(--pu-font-size-icon, 1.5em);
            padding: 0;
            color: var(--pu-color-icon-text, inherit);
            background-color: var(--pu-color-icon-background, inherit);
        }

        @supports (color: rgb(from white r g b)) {
            .button-icon {
                color: rgb(
                    from var(--pu-color-icon-text, inherit) r g b / alpha
                );
                background-color: rgb(
                    from var(--pu-color-icon-background, inherit) r g b / alpha
                );
            }
        }

        .button-icon > * {
            position: relative;
            display: block;
            padding: var(
                --pu-space-icon-padding,
                var(--pu-space-padding, 0.5em)
            );
        }

        .button-icon:hover {
            opacity: 0.7;
        }

        .button-icon[disabled]:hover {
            opacity: 0.7;
        }

        /* loading spinner animation */
        .loading__spinner {
            animation-name: spin;
            animation-duration: 500ms;
            animation-timing-function: cubic-bezier(0, 0, 1, 1);
            animation-iteration-count: infinite;
            fill: currentcolor;
            color: var(--pu-color-base-text, rgb(160, 160, 160));
            height: 3em;
            width: 3em;
            left: 50%;
            top: 50%;
            margin: calc(var(--pu-space-400, 1em) / 2 * -1) 0 0
                calc(var(--pu-space-400, 1em) / 2 * -1);
        }
        @keyframes spin {
            to {
                transform: rotate(1turn);
            }
        }
    `
}
